import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { AppLoadingState, AppText } from "../../ui-foundation";
import {
  DocumentList,
  DocumentPageScaffold,
  DocumentSection,
  DocumentSubsection,
} from "@/components/DocumentScaffold";
import { STEADY_HOUSE_LEGAL_NAME } from "../../shared/support";

const ModerationGuidelinesPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate("/my-kindred", { replace: true });
      return;
    }

    const check = async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .in("role", ["moderator", "admin"]);

      if (!data || data.length === 0) {
        navigate("/my-kindred", { replace: true });
      } else {
        setAuthorized(true);
      }

      setChecking(false);
    };

    void check();
  }, [user, loading, navigate]);

  if (loading || checking || !authorized) {
    return (
      <AppLoadingState
        title="Loading moderation guidance"
        body="Checking your access and preparing the internal guidance page."
      />
    );
  }

  return (
    <DocumentPageScaffold
      title="Admin & Moderation Guidance"
      updatedLabel="Internal use only · Last updated: March 2026"
      fallbackPath="/my-kindred"
      footerLinks={[{ href: "mailto:hello@kindredcommunity.app", label: "Contact" }]}
      footerNote="Internal Kindred guidance for moderators and administrators."
    >
      <DocumentSection title="Overview">
        <AppText>
          These guidelines explain how moderation and administration work within Kindred. They are for
          internal use only and apply to users with Moderator or Administrator roles. Their purpose is to
          keep the community safe, fair, and consistent, not to police opinions or personalities.
        </AppText>
        <AppText>{STEADY_HOUSE_LEGAL_NAME} is responsible for the moderation and safeguarding decisions made under this guidance.</AppText>
      </DocumentSection>

      <DocumentSection title="1. Roles & Responsibilities">
        <DocumentSubsection title="Members">
          <AppText>
            Members can create posts, comments, events, chat rooms, messages, and DMs. They can edit and
            delete their own content only. Members cannot moderate other users.
          </AppText>
        </DocumentSubsection>

        <DocumentSubsection title="Moderators">
          <AppText>Moderators are trusted community stewards.</AppText>
          <AppText>Moderators can:</AppText>
          <DocumentList
            items={[
              "Remove community feed posts or comments that breach the Code of Conduct",
              "Remove messages from public chat rooms",
              "Review and triage user reports",
              "De-escalate conflict by removing content rather than engaging publicly",
            ]}
          />
          <AppText>Moderators cannot:</AppText>
          <DocumentList
            items={[
              "Ban or unban users",
              "Assign or change user roles",
              "Access or moderate private DMs",
              "View or modify account-level data",
            ]}
          />
          <AppText>When in doubt, moderators should escalate to an Administrator rather than act.</AppText>
        </DocumentSubsection>

        <DocumentSubsection title="Administrators">
          <AppText>Administrators are responsible for the safety and governance of the platform.</AppText>
          <AppText>Administrators can:</AppText>
          <DocumentList
            items={[
              "Do everything a moderator can",
              "Assign or remove moderator and admin roles",
              "Ban or unban users",
              "Remove events or accounts where necessary",
              "Intervene in serious safety, legal, or safeguarding situations",
            ]}
          />
          <AppText>Administrator actions should be rare, deliberate, and proportionate.</AppText>
        </DocumentSubsection>
      </DocumentSection>

      <DocumentSection title="2. Moderation Principles">
        <DocumentSubsection title="Least-Force First">
          <AppText>
            If removing content solves the problem, do that. Avoid public confrontation, warnings, or
            commentary where possible.
          </AppText>
        </DocumentSubsection>
        <DocumentSubsection title="Neutral Tone">
          <AppText>Remain calm, factual, and non-judgmental. Never shame, mock, or argue with users.</AppText>
        </DocumentSubsection>
        <DocumentSubsection title="Content, Not Character">
          <AppText>Moderate what was posted, not who posted it.</AppText>
        </DocumentSubsection>
        <DocumentSubsection title="Consistency">
          <AppText>
            Apply the same standards regardless of personal relationships, disagreements, popularity, or
            length of membership.
          </AppText>
        </DocumentSubsection>
      </DocumentSection>

      <DocumentSection title="3. When to Remove Content">
        <AppText>Remove content when it includes:</AppText>
        <DocumentList
          items={[
            "Harassment, bullying, or intimidation",
            "Hate speech or discrimination",
            "Sexual content involving minors",
            "Threats of violence or self-harm",
            "Repeated disruption after prior moderation",
            "Clear breaches of the Code of Conduct",
          ]}
        />
        <AppText>If unsure, escalate.</AppText>
      </DocumentSection>

      <DocumentSection title="4. Reports & Escalation">
        <AppText>
          Reported content should be reviewed promptly, assessed against the Code of Conduct, and actioned
          quietly where possible.
        </AppText>
        <AppText>Escalate to an Administrator if:</AppText>
        <DocumentList
          items={[
            "A user repeatedly breaches rules",
            "There are safeguarding concerns",
            "There are threats, coercion, or stalking behaviour",
            "A ban may be required",
            "You are uncertain about the correct response",
          ]}
        />
        <AppText>Escalation is a strength, not a failure.</AppText>
      </DocumentSection>

      <DocumentSection title="5. Banning Users (Admin Only)">
        <AppText>Bans are a last resort. A ban may be appropriate when:</AppText>
        <DocumentList
          items={[
            "A user poses a safety risk",
            "There is repeated harmful behaviour",
            "Prior moderation has failed",
            "Legal or safeguarding concerns exist",
          ]}
        />
        <AppText>
          Bans should be documented internally, be reversible if circumstances change, and never be used for
          personal disagreements. Moderators must not attempt to ban users.
        </AppText>
      </DocumentSection>

      <DocumentSection title="6. Privacy & Boundaries">
        <DocumentList
          items={[
            "Private DMs are private",
            "Moderators do not access or moderate DMs",
            "Administrators should only intervene in DMs if there is a serious safety or legal concern",
            "User data must be treated as confidential at all times",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="7. What This Is Not">
        <AppText>
          These guidelines are not a replacement for empathy, a platform for personal disputes, or a rigid
          script. They exist to support fair decisions, not remove human judgment.
        </AppText>
      </DocumentSection>

      <DocumentSection title="8. Questions & Changes">
        <AppText>
          If something is unclear or missing, raise it with an Administrator. Do not improvise policy in
          public. These guidelines may evolve as the community grows.
        </AppText>
      </DocumentSection>

      <DocumentSection title="Summary">
        <DocumentList
          items={[
            "Moderators keep the space calm and safe",
            "Administrators handle serious or irreversible actions",
            "Quiet, consistent action is better than loud enforcement",
            "When unsure, escalate",
          ]}
        />
      </DocumentSection>
    </DocumentPageScaffold>
  );
};

export default ModerationGuidelinesPage;
